import dayjs, { type Dayjs } from 'dayjs'
import type { FC } from 'react'

import type { AvailabilityBlock } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleAvailability'

import { DayPreview, type EditedWindow } from './DayPreview'
import styles from './ehrAppointmentV2.module.scss'
import type { SearchCriteria, SlotOption } from './ehrAppointmentV2.types'
import { PreviewPlaceholder } from './PreviewPlaceholder'
import { ResultsList } from './ResultsList'
import { SearchBar } from './SearchBar'
import type { useAvailabilitySearch } from './useAvailabilitySearch'
import { roomHint, type useRoomFilter } from './useRoomFilter'
import type { useSelectedSlot } from './useSelectedSlot'

type Props = {
  criteria: SearchCriteria
  search: ReturnType<typeof useAvailabilitySearch>
  roomFilter: ReturnType<typeof useRoomFilter>
  picked: ReturnType<typeof useSelectedSlot>
  isEmr: boolean
  isDisabled?: boolean
  isDateDisabled?: (date: Dayjs) => boolean
  siteId?: string
  currentDay: string
  visitStart: number
  visitDuration: number
  patientName: string
  editedWindow?: EditedWindow
  timeZone?: string
  availabilityBlocks?: AvailabilityBlock[]
  onChangeCriteria: (patch: Partial<SearchCriteria>) => void
  onSelect: (slot: SlotOption) => void
}

export const AvailabilityPanel: FC<Props> = ({
  criteria,
  search,
  roomFilter,
  picked,
  isEmr,
  isDisabled,
  isDateDisabled,
  siteId,
  currentDay,
  visitStart,
  visitDuration,
  patientName,
  editedWindow,
  timeZone,
  availabilityBlocks,
  onChangeCriteria,
  onSelect,
}) => (
  <>
    <SearchBar
      criteria={criteria}
      isSearching={search.isFetching}
      canSearch={search.canSearch}
      isDisabled={isDisabled}
      isDateDisabled={isDateDisabled}
      onChange={onChangeCriteria}
      onSearch={search.run}
    />

    {search.hasSearched && (
      <div className={styles.results}>
        <ResultsList
          days={roomFilter.results}
          isFetching={search.isFetching || roomFilter.isFetching}
          hint={roomHint(roomFilter, isEmr ? 'Your EMR availability' : 'Your availability')}
          hasError={search.hasError}
          isUnreadable={search.isUnreadable}
          selectedKey={picked.selectedKey}
          isDisabled={isDisabled}
          onSelect={onSelect}
          onRetry={search.run}
        />
        {/* The calendar waits for a picked slot, so it never shows a day nobody chose. */}
        {picked.selectedSlot ? (
          <DayPreview
            siteId={siteId}
            day={currentDay}
            label={dayjs(currentDay).format('dddd, MMMM D, YYYY')}
            visitStart={visitStart}
            visitDuration={visitDuration}
            patientName={patientName}
            edited={editedWindow}
            timeZone={timeZone}
            roomBookings={roomFilter.previewBookings}
            roomName={roomFilter.roomName}
            availabilityBlocks={availabilityBlocks}
          />
        ) : (
          <PreviewPlaceholder />
        )}
      </div>
    )}
  </>
)
